import { useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, CheckCircle2, FileText, Mail, SendHorizontal, ShieldCheck, UserRound, UsersRound } from "lucide-react";
import { EVENTS } from "@/lib/eventsData";
import MessageWorkspaceNav, { MESSAGE_SECTIONS, type MessageSectionId } from "@/components/MessageWorkspaceNav";
import { chiNotify } from "@/components/ChiNotification";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import SearchableSelect from "@/components/SearchableSelect";
import MemberMessageReportsPanel from "@/components/MemberMessageReportsPanel";
import { isEligibleForMessageAudience, isMailableEventRegistration } from "@shared/messageAudience";

const surface: React.CSSProperties = { background: "rgba(13,18,32,0.82)", border: "1px solid rgba(255,255,255,0.075)", borderRadius: "0.95rem" };
const inputStyle: React.CSSProperties = { width: "100%", minHeight: 40, boxSizing: "border-box", border: "1px solid rgba(255,255,255,0.13)", borderRadius: "0.58rem", background: "rgba(255,255,255,0.045)", color: "#F8FAFC", padding: ".6rem .7rem", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" };

const TEMPLATES = {
  event_update: { label: "Event update", title: "Event update", subject: "An update from The Chi Alliance", body: "Hello {{first_name}},\n\nWe are sharing an important update from The Chi Alliance.\n\nThank you for being part of this community.\n\nThe Chi Alliance" },
  community_note: { label: "Community note", title: "Community note", subject: "A note from The Chi Alliance", body: "Hello {{first_name}},\n\nWe are grateful to have you in The Chi Alliance community.\n\nWith appreciation,\nThe Chi Alliance" },
  individual_follow_up: { label: "Individual follow-up", title: "Individual follow-up", subject: "Following up from The Chi Alliance", body: "Hello {{first_name}},\n\nThank you for connecting with The Chi Alliance. We wanted to follow up personally.\n\nWarmly,\nThe Chi Alliance" },
} as const;

type TemplateKey = keyof typeof TEMPLATES;
type AudienceMode = "individual" | "event" | "filtered";

/**
 * The three kinds of message, chosen before anything is written.
 *
 * Template and audience used to be two independent dropdowns, which allowed
 * combinations nobody wants — an individual follow-up addressed to a whole event
 * roster — and made the first decision ("who is this for?") look like a
 * formatting choice. Picking the kind of message sets both, and each kind opens
 * with its own recipient control: a person for a direct message, a roster for an
 * event update, tags for a community update.
 */
const START_TYPES = [
  {
    key: "direct" as const,
    label: "Direct Message",
    blurb: "One person, written to them.",
    icon: UserRound,
    template: "individual_follow_up" as TemplateKey,
    audience: "individual" as AudienceMode,
  },
  {
    key: "event" as const,
    label: "Event Update",
    blurb: "Everyone registered for one event.",
    icon: CalendarDays,
    template: "event_update" as TemplateKey,
    audience: "event" as AudienceMode,
  },
  {
    key: "community" as const,
    label: "Community Update",
    blurb: "A segment of the community, by tag.",
    icon: UsersRound,
    template: "community_note" as TemplateKey,
    audience: "filtered" as AudienceMode,
  },
];

type StartTypeKey = (typeof START_TYPES)[number]["key"];

/**
 * Who a draft goes to, as an explicit choice.
 *
 * The audience used to be decided silently by the kind of message — picking
 * "Event Update" set it to the event roster and nothing on screen said so. It is
 * the second question anyone actually asks, so it is asked, and choosing a kind
 * now only preselects the obvious answer rather than hiding it.
 */
const AUDIENCE_CHOICES = [
  {
    key: "individual" as AudienceMode,
    label: "One person",
    blurb: "A single attendee who has opted in.",
    icon: UserRound,
  },
  {
    key: "event" as AudienceMode,
    label: "An event roster",
    blurb: "Everyone registered or waitlisted for one event.",
    icon: CalendarDays,
  },
  {
    key: "filtered" as AudienceMode,
    label: "A community segment",
    blurb: "Members matching one or more tags.",
    icon: UsersRound,
  },
];

function formatEvent(eventSlug: string | null) {
  if (!eventSlug) return "Filtered audience";
  return EVENTS.find(event => event.slug === eventSlug)?.title ?? eventSlug;
}

type DeliveryChecklist = {
  dkim: boolean;
  spfDmarc: boolean;
  googleWorkspacePolicy: boolean;
  senderAuthorization: boolean;
  suppressionReview: boolean;
  seedInboxVerification: boolean;
};

type CampaignQueueItemModel = {
  id: string;
  title: string;
  subject: string;
  audienceKind: "individual" | "event" | "filtered";
  eventSlug: string | null;
  recipientSnapshotCount: number;
  status: string;
  deliverySummary: { pending: number; skipped: number; sent: number; failed: number };
};

function CampaignQueueItem({
  campaign,
  active,
  senderReady,
  senderEmail,
  senderReason,
  checklist,
  onToggleChecklist,
  confirmationText,
  onConfirmationTextChange,
  onToggle,
  onConfirm,
  isDelivering,
}: {
  campaign: CampaignQueueItemModel;
  active: boolean;
  senderReady: boolean;
  senderEmail: string | null | undefined;
  senderReason: string | null | undefined;
  checklist: DeliveryChecklist;
  onToggleChecklist: (key: keyof DeliveryChecklist) => void;
  confirmationText: string;
  onConfirmationTextChange: (value: string) => void;
  onToggle: () => void;
  onConfirm: () => void;
  isDelivering: boolean;
}) {
  const isReviewable = campaign.status === "ready_for_review";
  const allChecksComplete = Object.values(checklist).every(Boolean);
  const summary = campaign.deliverySummary;
  const canConfirm = senderReady && allChecksComplete && confirmationText === "SEND" && !isDelivering;
  const checklistItems: [keyof DeliveryChecklist, string][] = [
    ["dkim", "DKIM is enabled and aligned for the sender."],
    ["spfDmarc", "SPF and DMARC have been reviewed."],
    ["googleWorkspacePolicy", "Workspace spam and quarantine policies allow this outreach."],
    ["senderAuthorization", "Sender or alias authorization and forwarding rules were reviewed."],
    ["suppressionReview", "Suppression handling was reviewed."],
    ["seedInboxVerification", "A representative branded message was verified in a seed inbox."],
  ];

  return <div style={{ padding: ".7rem .75rem", borderRadius: ".6rem", background: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.06)" }}>
    <div style={{ display: "grid", gridTemplateColumns: "minmax(190px,1.2fr) minmax(170px,.8fr) 90px 115px", gap: ".75rem", alignItems: "center" }} className="campaign-row">
      <div><strong style={{ display: "block", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>{campaign.title}</strong><span style={{ color: "#7C899D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{campaign.subject}</span></div>
      <span style={{ color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{campaign.audienceKind === "event" ? formatEvent(campaign.eventSlug) : campaign.audienceKind === "individual" ? "Individual audience" : "Filtered audience"}</span>
      <span style={{ color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, textTransform: "uppercase" }}>{campaign.recipientSnapshotCount} recipients</span>
      <span style={{ justifySelf: "start", padding: ".28rem .45rem", borderRadius: "999px", color: "#7DEAFF", background: "rgba(0,212,255,.09)", border: "1px solid rgba(0,212,255,.2)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{campaign.status.replaceAll("_", " ")}</span>
    </div>
    {(summary.sent > 0 || summary.failed > 0 || summary.skipped > 0) && <p style={{ margin: ".65rem 0 0", color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Delivery outcomes: {summary.sent} sent · {summary.skipped} not attempted · {summary.failed} failed. Recipient addresses are not shown here.</p>}
    {isReviewable && <div style={{ marginTop: ".75rem", paddingTop: ".75rem", borderTop: "1px solid rgba(255,255,255,.08)" }}>
      <button type="button" onClick={onToggle} style={{ minHeight: 38, padding: ".5rem .7rem", border: "1px solid rgba(125,234,255,.36)", borderRadius: ".55rem", background: active ? "rgba(0,212,255,.13)" : "rgba(0,212,255,.06)", color: "#B9F4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}>{active ? "Close delivery confirmation" : "Review delivery confirmation"}</button>
      {active && <div style={{ display: "grid", gap: ".75rem", marginTop: ".75rem", padding: ".85rem", borderRadius: ".6rem", background: "rgba(0,0,0,.16)", border: "1px solid rgba(233,30,140,.22)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: ".5rem", color: senderReady ? "#86EFAC" : "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{senderReady ? <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />}{senderReady ? `Authorized sender ready: ${senderEmail}.` : senderReason || "A connected Google Workspace sender is required before delivery."}</div>
        <fieldset style={{ margin: 0, padding: ".7rem", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".55rem" }}><legend style={{ padding: "0 .25rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>Manual delivery readiness</legend><div style={{ display: "grid", gap: ".5rem" }}>{checklistItems.map(([key, label]) => <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: ".45rem", color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45, cursor: "pointer" }}><input type="checkbox" checked={checklist[key]} onChange={() => onToggleChecklist(key)} />{label}</label>)}</div></fieldset>
        <label style={{ display: "grid", gap: ".35rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".06em", textTransform: "uppercase" }}>Type SEND to begin delivery<input value={confirmationText} onChange={event => onConfirmationTextChange(event.target.value)} maxLength={4} placeholder="SEND" style={inputStyle} /></label>
        <div style={{ display: "flex", alignItems: "flex-start", gap: ".45rem", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}><AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> This action begins delivery to currently eligible recipients. Consent and sender readiness are re-checked server-side immediately before each message is attempted.</div>
        <button type="button" disabled={!canConfirm} onClick={onConfirm} style={{ minHeight: 42, justifySelf: "start", padding: ".62rem .8rem", border: "1px solid rgba(233,30,140,.55)", borderRadius: ".55rem", background: canConfirm ? "linear-gradient(135deg,#E91E8C,#B91566)" : "rgba(255,255,255,.06)", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: canConfirm ? "pointer" : "not-allowed", opacity: isDelivering ? .7 : 1 }}><SendHorizontal size={15} style={{ verticalAlign: "-3px", marginRight: ".35rem" }} /> {isDelivering ? "Delivering…" : "Confirm and begin delivery"}</button>
      </div>}
    </div>}
  </div>;
}

export default function MessageCampaignView() {
  const utils = trpc.useUtils();
  const registryQuery = trpc.attendees.registry.useQuery(undefined, { retry: false });
  /*
   * The whole member roster, so the recipient list is the community rather than
   * only the people who happen to have registered for an event.
   *
   * A campaign still targets an attendee profile — that is what the audience
   * contract resolves against — so a member who has never registered has nothing
   * to target. Those are listed and marked rather than omitted: searching for a
   * colleague who is plainly a member and finding an empty list says nothing,
   * while a greyed row saying why answers it.
   */
  const memberRosterQuery = trpc.members.adminRecords.useQuery(undefined, { retry: false });
  const deliveryReadinessQuery = trpc.attendees.deliveryReadiness.useQuery(undefined, { retry: false });
  const [templateKey, setTemplateKey] = useState<TemplateKey>("event_update");
  const [audienceMode, setAudienceMode] = useState<AudienceMode>("event");
  /*
   * No kind is chosen to begin with.
   *
   * It used to default to "event", so the form and its preview were on screen
   * before anyone had said what they were sending — twenty fields answering a
   * question that had not been asked. Nothing below the choice renders until the
   * choice is made.
   */
  const [startType, setStartType] = useState<StartTypeKey | null>(null);
  const [section, setSection] = useState<MessageSectionId>("create");
  const activeSection = MESSAGE_SECTIONS.find(entry => entry.id === section) ?? null;
  const [eventSlug, setEventSlug] = useState("");
  const [selectedAttendeeId, setSelectedAttendeeId] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [title, setTitle] = useState<string>(TEMPLATES.event_update.title);
  const [subject, setSubject] = useState<string>(TEMPLATES.event_update.subject);
  const [bodyText, setBodyText] = useState<string>(TEMPLATES.event_update.body);
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
  const [recipientPreviewOpen, setRecipientPreviewOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [readinessChecklist, setReadinessChecklist] = useState({
    dkim: false,
    spfDmarc: false,
    googleWorkspacePolicy: false,
    senderAuthorization: false,
    suppressionReview: false,
    seedInboxVerification: false,
  });

  const registry = registryQuery.data?.attendees ?? [];
  const tags = registryQuery.data?.tags ?? [];
  const campaigns = registryQuery.data?.campaigns ?? [];
  const eligibleRecipients = useMemo(() => registry.filter(item => {
    if (audienceMode === "event") return Boolean(
      isEligibleForMessageAudience(item.profile.communicationConsent, "event")
      && eventSlug
      && item.registrations.some(registration => registration.eventSlug === eventSlug && isMailableEventRegistration(registration)),
    );
    if (!isEligibleForMessageAudience(item.profile.communicationConsent, audienceMode)) return false;
    if (audienceMode === "individual") return item.profile.memberOpenId === selectedAttendeeId;
    const matchesEvent = !eventSlug || item.registrations.some(registration => registration.eventSlug === eventSlug && isMailableEventRegistration(registration));
    const matchesTags = selectedTagIds.length === 0 || item.tags.some(tag => selectedTagIds.includes(tag.id));
    return matchesEvent && matchesTags;
  }), [registry, audienceMode, eventSlug, selectedAttendeeId, selectedTagIds]);
  const sortedEligibleRecipients = useMemo(() => [...eligibleRecipients].sort((left, right) => {
    const byName = left.profile.fullName.localeCompare(right.profile.fullName, undefined, { sensitivity: "base" });
    return byName || left.profile.email.localeCompare(right.profile.email, undefined, { sensitivity: "base" });
  }), [eligibleRecipients]);

  const prepareCampaign = trpc.attendees.prepareMessageCampaign.useMutation({
    onSuccess: async data => {
      await utils.attendees.registry.invalidate();
      chiNotify.success("Message draft prepared", { description: data.notice });
    },
    onError: error => chiNotify.error("Draft not prepared", { description: error.message || "Review the audience and required content." }),
  });

  const confirmDelivery = trpc.attendees.confirmAndDeliverMessageCampaign.useMutation({
    onSuccess: async data => {
      await Promise.all([
        utils.attendees.registry.invalidate(),
        utils.attendees.deliveryReadiness.invalidate(),
      ]);
      setActiveCampaignId(null);
      setConfirmationText("");
      setReadinessChecklist({ dkim: false, spfDmarc: false, googleWorkspacePolicy: false, senderAuthorization: false, suppressionReview: false, seedInboxVerification: false });
      chiNotify.success("Campaign delivery completed", { description: data.notice });
    },
    onError: error => chiNotify.error("Campaign not delivered", { description: error.message || "Review sender readiness and current recipient consent." }),
  });

  function switchTemplate(next: TemplateKey) {
    setTemplateKey(next);
    setTitle(TEMPLATES[next].title);
    setSubject(TEMPLATES[next].subject);
    setBodyText(TEMPLATES[next].body);
  }

  function toggleTag(id: number) {
    setSelectedTagIds(current => current.includes(id) ? current.filter(tagId => tagId !== id) : [...current, id]);
  }

  function toggleReadinessCheck(key: keyof typeof readinessChecklist) {
    setReadinessChecklist(current => ({ ...current, [key]: !current[key] }));
  }

  function prepareDraft(event: React.FormEvent) {
    event.preventDefault();

    /*
     * A member the campaign cannot reach is refused here, with the reason, rather
     * than at the picker.
     *
     * The audience resolves to an attendee profile id; a member without one has
     * nothing to target, and submitting sent `NaN`. Selecting them is still
     * allowed — being told why they cannot be mailed is more use than a name that
     * refuses to be clicked.
     */
    if (audienceMode === "individual" && selectedAttendeeId.startsWith("unreachable:")) {
      chiNotify.error("That member cannot receive a campaign yet", {
        description: "They appear once they have registered for an event and opted in to outreach. Until then, use a private conversation instead.",
      });
      return;
    }

    const audience = audienceMode === "individual"
      ? { audienceKind: "individual" as const, memberOpenIds: [selectedAttendeeId] }
      : audienceMode === "event"
        ? { audienceKind: "event" as const, eventSlug }
        : { audienceKind: "filtered" as const, ...(eventSlug ? { eventSlug } : {}), tagIds: selectedTagIds };
    prepareCampaign.mutate({
      title,
      audience,
      subject,
      bodyText,
      templateKey,
      filterSummary: audienceMode === "filtered" ? `Event: ${eventSlug || "all"}; tags: ${selectedTagIds.length ? selectedTagIds.join(",") : "all"}` : undefined,
    });
  }

  return (
    <div style={{ padding: "clamp(1.25rem, 3vw, 2.5rem)", width: "100%", boxSizing: "border-box" }}>
      <header style={{ marginBottom: "1.5rem" }}>
        <p style={{ margin: "0 0 .35rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".12em", textTransform: "uppercase" }}>Admin CRM · Controlled outreach</p>
        <h1 style={{ margin: 0, color: "#FFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3.1rem)", letterSpacing: ".025em", lineHeight: 1 }}>Messages</h1>
        <p style={{ margin: ".65rem 0 0", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, maxWidth: 800 }}>Prepare branded, consent-aware drafts for one attendee, an event roster, or a filtered segment. Preparing a draft never sends mail; a separate final-confirmation delivery workflow remains required.</p>
      </header>

      <MemberMessageReportsPanel />

      <style>{`
        .message-start-types {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
          gap: .6rem;
          margin-bottom: .4rem;
        }

        .message-start-types button {
          display: flex;
          align-items: flex-start;
          gap: .6rem;
          padding: .85rem .9rem;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: .8rem;
          background: rgba(255,255,255,.03);
          color: #8E9AAF;
          text-align: left;
          cursor: pointer;
          transition: border-color .16s ease, background .16s ease, color .16s ease;
        }

        /*
          The icon keeps its own size.

          The button is a flex row, so the icon was the flexible item and got
          squeezed to 19px wide while keeping its 26px height — a squashed glyph
          rather than a larger one.
        */
        .message-start-types button > svg { flex: 0 0 auto; }

        .message-start-types button:hover { border-color: rgba(233,30,140,.32); color: #fff; }

        .message-start-types button[data-active] {
          border-color: rgba(233,30,140,.5);
          background: linear-gradient(135deg, rgba(233,30,140,.16), rgba(124,58,237,.1));
          color: #fff;
        }

        .message-start-types strong {
          display: block;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 750;
        }

        .message-start-types em {
          display: block;
          margin-top: .12rem;
          color: #7E8CA0;
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          font-style: normal;
          line-height: 1.4;
        }

        @media (prefers-reduced-motion: reduce) {
          .message-start-types button { transition: none; }
        }
      `}</style>

      {/*
        The workspace, with its own nav.

        Messages had grown into one long page holding a composer, a delivery queue
        and a moderation panel, so unrelated work shared one scroll. Each section
        now stands on its own, and the nav is what says which ones exist.
      */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 232px) minmax(0, 1fr)", gap: "1.15rem", alignItems: "start" }} className="message-workspace-shell">
        <MessageWorkspaceNav active={section} onSelect={setSection} />
        <div style={{ minWidth: 0 }}>
          {section === "create" && (<>
          <section style={{ display: "grid", gridTemplateColumns: "minmax(0,1.18fr) minmax(320px,.82fr)", gap: "1rem", alignItems: "start" }} className="message-workspace-grid">
            <form onSubmit={prepareDraft} style={{ ...surface, padding: "clamp(1rem, 2.5vw, 1.4rem)" }}>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center", marginBottom: ".85rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "calc(var(--chi-body-compact) * 1.05)", fontWeight: 750, letterSpacing: ".01em" }}><FileText size={15} /> What type of message would you like to send?</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: ".75rem" }} className="message-form-grid">
                {/*
                  One choice instead of two dropdowns. Switching kind reloads the
                  template copy, so a draft that has been edited is replaced — which
                  is why the confirmation asks first.
                */}
                <div role="radiogroup" aria-label="Kind of message" className="message-start-types" style={{ gridColumn: "1 / -1" }}>
                  {START_TYPES.map(type => {
                    const Icon = type.icon;
                    const active = startType === type.key;
                    return (
                      <button
                        key={type.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        data-active={active || undefined}
                        onClick={() => {
                          if (active) return;
                          setStartType(type.key);
                          setAudienceMode(type.audience);
                          switchTemplate(type.template);
                        }}
                      >
                        <Icon size={26} strokeWidth={1.9} aria-hidden="true" />
                        <span>
                          <strong>{type.label}</strong>
                          <em>{type.blurb}</em>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {startType && (<>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center", margin: "1.35rem 0 .85rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "calc(var(--chi-body-compact) * 1.05)", fontWeight: 750, letterSpacing: ".01em" }}>
                <UsersRound size={15} /> Who would you like to send this to?
              </div>
              <div role="radiogroup" aria-label="Who to send to" className="message-start-types">
                {AUDIENCE_CHOICES.map(choice => {
                  const Icon = choice.icon;
                  const active = audienceMode === choice.key;
                  return (
                    <button
                      key={choice.key}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-active={active || undefined}
                      onClick={() => setAudienceMode(choice.key)}
                    >
                      <Icon size={26} strokeWidth={1.9} aria-hidden="true" />
                      <span>
                        <strong>{choice.label}</strong>
                        <em>{choice.blurb}</em>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: ".75rem" }}>
                {audienceMode === "individual" && (() => {
                  /*
                   * Only explicit outreach opt-ins may be drafted to, so this list is
                   * frequently empty. It used to render as a required select containing
                   * nothing but "Select an attendee…", which is a dead end: the form
                   * cannot be submitted and nothing on screen says why. The control now
                   * reports whether it is still loading, whether anyone has opted in, and
                   * how many did.
                   */
                  const optedIn = registry.filter(item => item.profile.communicationConsent === "opted_in");
                  const loading = registryQuery.isLoading || memberRosterQuery.isLoading;

                  /*
                   * Every member, with the reachable ones choosable.
                   *
                   * A campaign resolves its audience to attendee profiles, so a member
                   * is reachable only if they have one and it has opted in. Matched on
                   * email because that is the only identifier the two lists share.
                   */
                  const reachableByEmail = new Map(
                    optedIn
                      .filter(item => item.profile.email)
                      .map(item => [item.profile.email!.trim().toLocaleLowerCase(), item]),
                  );
                  const roster = memberRosterQuery.data ?? [];
                  const memberOptions = roster
                    .map(record => {
                      const email = record.email?.trim().toLocaleLowerCase() ?? "";
                      const match = email ? reachableByEmail.get(email) : undefined;
                      return {
                        value: match ? match.profile.memberOpenId : `unreachable:${record.userOpenId}`,
                        label: record.name || record.email || "Unnamed member",
                        hint: match
                          ? record.email ?? undefined
                          : `${record.email ?? "no email"} · not reachable by campaign`,
                        email: record.email ?? null,
                        /*
                         * Selectable either way. Disabling the unreachable rows made
                         * the list look broken — every name visible, none choosable —
                         * so reachability is stated on the row and enforced at submit,
                         * where it can explain itself.
                         */
                        reachable: Boolean(match),
                      };
                    })
                    .sort((left, right) => Number(right.reachable) - Number(left.reachable)
                      || left.label.localeCompare(right.label));
                  const reachableCount = memberOptions.filter(option => option.reachable).length;
                  const selectedMember = memberOptions.find(option => option.value === selectedAttendeeId) ?? null;
                  const unavailable = loading || memberOptions.length === 0;
                  return (
                    <label style={{ display: "grid", gap: ".35rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>
                      Member
                      {/*
                        A type-ahead rather than a plain select: the opted-in list
                        grows with the community, and finding one person in a long
                        dropdown with no search is the problem this control exists to
                        solve. The email is the hint, so two people with the same name
                        are still tellable apart.
                      */}
                      <SearchableSelect
                        ariaLabel="Recipient"
                        placeholder={loading ? "Loading members…" : memberOptions.length === 0 ? "No members recorded yet" : "Search all members…"}
                        searchPlaceholder="Type a name or email…"
                        emptyLabel="No member matches that"
                        disabled={unavailable}
                        value={selectedAttendeeId || null}
                        onChange={setSelectedAttendeeId}
                        options={memberOptions.map(({ value, label, hint }) => ({ value, label, hint }))}
                      />
                      {/*
                        The chosen recipient, restated. A closed dropdown shows one
                        line of text and no confirmation of who was picked, which on a
                        roster with similar names is exactly where a draft goes to the
                        wrong person.
                      */}
                      {selectedMember && (
                        <div style={{ display: "flex", alignItems: "center", gap: ".7rem", padding: ".7rem .8rem", border: `1px solid ${selectedMember.reachable ? "rgba(134,239,172,.32)" : "rgba(252,211,77,.35)"}`, borderRadius: ".6rem", background: selectedMember.reachable ? "rgba(134,239,172,.07)" : "rgba(252,211,77,.07)" }}>
                          <span aria-hidden="true" style={{ display: "grid", placeItems: "center", flexShrink: 0, width: 38, height: 38, borderRadius: "50%", background: "rgba(233,30,140,.18)", border: "1px solid rgba(233,30,140,.35)", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800 }}>
                            {selectedMember.label.trim().charAt(0).toUpperCase()}
                          </span>
                          <span style={{ display: "grid", gap: ".1rem", minWidth: 0 }}>
                            <strong style={{ color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}>
                              {selectedMember.label.trim().split(/\s+/)[0]}
                            </strong>
                            <span style={{ color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", overflowWrap: "anywhere" }}>
                              {selectedMember.email ?? "No email on file"}
                            </span>
                            {!selectedMember.reachable && (
                              <span style={{ color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "calc(var(--chi-body-compact) * 0.9)" }}>
                                A campaign cannot reach them yet — they register for an event and opt in to outreach first.
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedAttendeeId("")}
                            style={{ marginLeft: "auto", flexShrink: 0, padding: ".25rem .5rem", border: "1px solid rgba(255,255,255,.14)", borderRadius: ".45rem", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}
                          >
                            Change
                          </button>
                        </div>
                      )}
                      <span id="opted-in-attendee-help" style={{ color: "#728096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>
                        {memberRosterQuery.error
                          ? `Members could not be loaded. ${memberRosterQuery.error.message}`
                          : loading
                            ? "Reading the member roster."
                            : memberOptions.length === 0
                              ? "No members are recorded yet."
                              : `${memberOptions.length} member${memberOptions.length === 1 ? "" : "s"} listed · ${reachableCount} reachable by campaign. A member becomes reachable once they register for an event and opt in to outreach.`}
                      </span>
                    </label>
                  );
                })()}
                {(audienceMode === "event" || audienceMode === "filtered") && <label style={{ display: "grid", gap: ".35rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>{audienceMode === "event" ? "Event roster" : "Limit to an event (optional)"}<select required={audienceMode === "event"} value={eventSlug} onChange={event => setEventSlug(event.target.value)} style={inputStyle}><option value="">{audienceMode === "event" ? "Select an event…" : "All recorded events"}</option>{EVENTS.map(event => <option key={event.slug} value={event.slug}>{event.title}</option>)}</select></label>}
                {audienceMode === "filtered" && <fieldset style={{ margin: ".8rem 0 0", padding: ".75rem", border: "1px solid rgba(255,255,255,.09)", borderRadius: ".65rem" }}><legend style={{ padding: "0 .3rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Match one or more tags</legend><div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap" }}>{tags.length ? tags.map(tag => <label key={tag.id} style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", padding: ".35rem .48rem", borderRadius: "999px", color: tag.color, background: selectedTagIds.includes(tag.id) ? `${tag.color}20` : "rgba(255,255,255,.035)", border: `1px solid ${selectedTagIds.includes(tag.id) ? tag.color : "rgba(255,255,255,.09)"}`, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}><input type="checkbox" checked={selectedTagIds.includes(tag.id)} onChange={() => toggleTag(tag.id)} />{tag.name}</label>) : <span style={{ color: "#728096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>No tags are available yet.</span>}</div></fieldset>}
              </div>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center", margin: "1.35rem 0 .85rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "calc(var(--chi-body-compact) * 1.05)", fontWeight: 750, letterSpacing: ".01em" }}>
                <FileText size={15} /> Now write the message
              </div>

              <label style={{ display: "grid", gap: ".35rem", marginTop: ".8rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Internal draft title<input required value={title} onChange={event => setTitle(event.target.value)} maxLength={160} style={inputStyle} /></label>
              <label style={{ display: "grid", gap: ".35rem", marginTop: ".8rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Email subject<input required value={subject} onChange={event => setSubject(event.target.value)} maxLength={180} style={inputStyle} /></label>
              <label style={{ display: "grid", gap: ".35rem", marginTop: ".8rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Message text<textarea required value={bodyText} onChange={event => setBodyText(event.target.value)} minLength={20} maxLength={8000} rows={10} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} /></label>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", marginTop: "1rem", paddingTop: ".9rem", borderTop: "1px solid rgba(255,255,255,.08)", flexWrap: "wrap" }}>
                {eligibleRecipients.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setRecipientPreviewOpen(true)}
                    aria-haspopup="dialog"
                    style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".5rem .65rem", margin: "-.5rem -.65rem", border: "1px solid transparent", borderRadius: ".55rem", background: "transparent", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45, textAlign: "left", textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" }}
                  >
                    <ShieldCheck size={15} style={{ flexShrink: 0 }} /> {eligibleRecipients.length} {audienceMode === "event" ? "registered or waitlisted" : "opted-in"} recipient{eligibleRecipients.length === 1 ? "" : "s"} will be placed in review only.
                  </button>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}><ShieldCheck size={15} /> 0 {audienceMode === "event" ? "registered or waitlisted" : "opted-in"} recipients will be placed in review only.</span>
                )}
                <button type="submit" disabled={prepareCampaign.isPending || eligibleRecipients.length === 0} style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".65rem .85rem", border: "1px solid rgba(233,30,140,.45)", borderRadius: ".6rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: prepareCampaign.isPending ? "wait" : "pointer" }}><SendHorizontal size={15} /> {prepareCampaign.isPending ? "Preparing…" : "Prepare for review"}</button>
              </div>
              </>)}
            </form>

            <Dialog open={recipientPreviewOpen} onOpenChange={setRecipientPreviewOpen}>
              <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl overflow-hidden border-white/10 bg-[#111827] text-white">
                <DialogHeader>
                  <DialogTitle>{audienceMode === "event" ? "Event update recipients" : "Opted-in recipients"}</DialogTitle>
                  <DialogDescription className="text-slate-300">
                    {audienceMode === "event"
                      ? `${sortedEligibleRecipients.length} currently registered or waitlisted recipient${sortedEligibleRecipients.length === 1 ? "" : "s"} will be placed in review.`
                      : `${sortedEligibleRecipients.length} opted-in recipient${sortedEligibleRecipients.length === 1 ? "" : "s"} will be placed in review.`}
                  </DialogDescription>
                </DialogHeader>
                <div aria-label="Recipients" role="list" className="min-h-0 max-h-[min(60dvh,32rem)] overflow-y-auto rounded-lg border border-white/10">
                  {sortedEligibleRecipients.map(({ profile }) => (
                    <div key={profile.id} role="listitem" className="grid gap-1 border-b border-white/10 px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] sm:items-center sm:gap-4">
                      <strong className="min-w-0 break-words font-['Space_Grotesk'] text-sm text-white">{profile.fullName || "Unnamed recipient"}</strong>
                      <span className="min-w-0 break-all font-['Inter'] text-sm text-slate-300 sm:text-right">{profile.email}</span>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            <aside style={{ display: "grid", gap: "1rem" }}>
              {startType && (<section style={{ ...surface, overflow: "hidden" }}><div style={{ padding: ".7rem .9rem", color: "#7DEAFF", background: "rgba(0,212,255,.07)", borderBottom: "1px solid rgba(0,212,255,.13)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".1em", textTransform: "uppercase" }}>Branded preview · not sent</div><div style={{ padding: "1.15rem" }}><div style={{ display: "flex", alignItems: "center", gap: ".55rem", marginBottom: "1rem" }}><img src="/media/brand/chi-flower-symbol.png" alt="The Chi Alliance" style={{ width: 42, height: 42, objectFit: "contain" }} /><div><p style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "var(--chi-body-compact)" }}>The Chi Alliance</p><p style={{ margin: ".12rem 0 0", color: "#7C899D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>Community message preview</p></div></div><p style={{ margin: "0 0 .6rem", color: "#7C899D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".07em", textTransform: "uppercase" }}>Subject</p><p style={{ margin: "0 0 1rem", color: "#FFF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>{subject || "Your subject will appear here"}</p><div style={{ whiteSpace: "pre-wrap", color: "#C7D0DF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65 }}>{bodyText || "Your message text will appear here."}</div></div></section>)}
              <section style={{ ...surface, padding: "1rem" }}><div style={{ display: "flex", gap: ".45rem", alignItems: "center", color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".1em", textTransform: "uppercase" }}><CheckCircle2 size={14} /> Delivery safeguards</div><ul style={{ margin: ".65rem 0 0", paddingLeft: "1.05rem", color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65 }}><li>{audienceMode === "event" ? "Only current registered or waitlisted attendees can enter this event-update draft." : "Only explicit outreach opt-ins can enter the draft."}</li><li>Each draft remains unsent until a separate final confirmation.</li><li>Sender readiness and current audience eligibility are re-checked at delivery time.</li></ul></section>
            </aside>
          </section>
          </>)}
          {section === "campaigns" && (<>
          <section style={{ ...surface, padding: "1rem", marginTop: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".45rem", marginBottom: ".7rem", color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".1em", textTransform: "uppercase" }}><Mail size={14} /> Prepared campaign queue</div>
            {campaigns.length ? <div style={{ display: "grid", gap: ".55rem" }}>{campaigns.map(campaign => <CampaignQueueItem
              key={campaign.id}
              campaign={campaign}
              active={activeCampaignId === campaign.id}
              senderReady={Boolean(deliveryReadinessQuery.data?.ready)}
              senderEmail={deliveryReadinessQuery.data?.senderEmail}
              senderReason={deliveryReadinessQuery.data?.reason}
              checklist={readinessChecklist}
              onToggleChecklist={toggleReadinessCheck}
              confirmationText={confirmationText}
              onConfirmationTextChange={setConfirmationText}
              onToggle={() => {
                setActiveCampaignId(activeCampaignId === campaign.id ? null : campaign.id);
                setConfirmationText("");
              }}
              onConfirm={() => confirmDelivery.mutate({
                campaignId: campaign.id,
                confirmationText: "SEND",
                readinessChecklist: {
                  dkim: true,
                  spfDmarc: true,
                  googleWorkspacePolicy: true,
                  senderAuthorization: true,
                  suppressionReview: true,
                  seedInboxVerification: true,
                },
              })}
              isDelivering={confirmDelivery.isPending}
            />)}</div> : <div style={{ display: "flex", alignItems: "center", gap: ".6rem", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}><UsersRound size={16} /> No campaign drafts have been prepared. No email has been sent.</div>}
          </section>
          </>)}
          {activeSection && !activeSection.built && (
            <section style={{ ...surface, padding: "clamp(1.1rem, 3vw, 1.6rem)", display: "grid", gap: ".6rem" }}>
              <p style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: ".45rem", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>Not built yet</p>
              <h2 style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "calc(var(--chi-body-compact) * 1.25)" }}>{activeSection.label}</h2>
              <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6, maxWidth: "46rem" }}>{activeSection.summary}</p>
              {/* Said plainly rather than shown as an empty table, which reads as a fault. */}
              <p style={{ margin: 0, color: "#7C899D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>Nothing on the server supports this yet, so there is no data to show. Create a Message and Campaigns are the two sections that work today.</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
